import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DeviceInfo } from '@capacitor/device';
import lookie from 'lookie';
import { CURRENT_ANDROID_ID } from '@/constant';
import { RootState } from './index';

export type State = {
  isAceConnect: boolean;
  deviceUuid: string;
  storeInfo: StoreInfo;
  exclusiveImageInfo: GetExclusiveImagesResponse;
  isCanClickTabBar: boolean;
  isCanNavToMassageControl: boolean;
  apkUpdateStatus: ApkUpdateStatus;
  devicePlatform: DeviceInfo['platform'];
  /** 在AI问询流程后是否需要提示更新H5 */
  shouldAlertUpdateH5AfterAIConsultation: boolean;
  apkVersion: string;
};

const initialState: State = {
  isAceConnect: false,
  deviceUuid: null,
  storeInfo: {} as any,
  exclusiveImageInfo: {
    logoImage: '',
    backgroundImage: '',
  },
  isCanClickTabBar: true,
  isCanNavToMassageControl: true,
  apkUpdateStatus: 'checking',
  devicePlatform: null,
  shouldAlertUpdateH5AfterAIConsultation: false,
  apkVersion: '1.0.0',
};

const device = createSlice({
  name: 'device',
  initialState,
  reducers: {
    setIsAceConnect(state, { payload }: PayloadAction<boolean>) {
      state.isAceConnect = payload;
    },
    setDeviceUuid(state, { payload }: PayloadAction<string>) {
      // 如果之前本地已经存储了androidId, 则使用本地的
      const previousAndroidId = lookie.get(CURRENT_ANDROID_ID);
      if (previousAndroidId) {
        state.deviceUuid = previousAndroidId;
      } else {
        lookie.set(CURRENT_ANDROID_ID, payload);
        state.deviceUuid = payload;
      }
    },
    setApkVersion(state, { payload }: PayloadAction<string>) {
      state.apkVersion = payload;
    },
    setStoreInfo(state, { payload }: PayloadAction<StoreInfo>) {
      state.storeInfo = payload;
    },
    setExclusiveImageInfo(state, { payload }: PayloadAction<GetExclusiveImagesResponse>) {
      state.exclusiveImageInfo = payload;
    },
    setIsCanClickTabBar(state, { payload }: PayloadAction<boolean>) {
      state.isCanClickTabBar = payload;
    },
    setIsCanNavToMassageControl(state, { payload }: PayloadAction<boolean>) {
      state.isCanNavToMassageControl = payload;
    },
    setApkUpdateStatus(state, { payload }: PayloadAction<ApkUpdateStatus>) {
      state.apkUpdateStatus = payload;
    },
    setDevicePlatform(state, { payload }: PayloadAction<DeviceInfo['platform']>) {
      state.devicePlatform = payload;
    },
    setShouldAlertUpdateH5AfterAIConsultation(state, { payload }: PayloadAction<boolean>) {
      state.shouldAlertUpdateH5AfterAIConsultation = payload;
    },
  },
});

export const {
  setIsAceConnect,
  setDeviceUuid,
  setApkVersion,
  setStoreInfo,
  setExclusiveImageInfo,
  setIsCanClickTabBar,
  setIsCanNavToMassageControl,
  setApkUpdateStatus,
  setDevicePlatform,
  setShouldAlertUpdateH5AfterAIConsultation,
} = device.actions;

export const selectedIsAceConnect = (state: RootState) => state.device.isAceConnect;
export const selectedDeviceUuid = (state: RootState) => state.device.deviceUuid;
export const selectedApkVersion = (state: RootState) => state.device.apkVersion;
export const selectedStoreInfo = (state: RootState) => state.device.storeInfo;
export const selectedExclusiveImageInfo = (state: RootState) => state.device.exclusiveImageInfo;
export const selectedIsCanClickTabBar = (state: RootState) => state.device.isCanClickTabBar;
export const selectedIsCanNavToMassageControl = (state: RootState) => state.device.isCanNavToMassageControl;
export const selectedApkUpdateStatus = (state: RootState) => state.device.apkUpdateStatus;
export const selectDevicePlatform = (state: RootState) => state.device.devicePlatform;
export const selectShouldAlertUpdateH5AfterAIConsultation = (state: RootState) =>
  state.device.shouldAlertUpdateH5AfterAIConsultation;

export default device.reducer;
